import { Calendar, ChevronRight, Paperclip, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { InspectionStatus, type InspectionType } from '@/common/inspection';
import type { WithDeletedBy } from '@/common/soft-delete';
import { DamageIcon, InspectionIcon } from '@/components/entity-icons';
import { DeletedBadge, DeletedOnIcon } from '@/components/soft-delete/DeletedInfo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { deletedPreviewHref } from '@/hooks/use-deleted-preview';
import { cn, formatDateOnly, getInspectionStatusLabel } from '@/lib/utils';

export type InpsectionCardProps = {
	inspection: WithDeletedBy<InspectionType>;
	/** Deleted-items view (GH #331): the card shows when it was deleted and a Restore button, and opens read-only. */
	onRestore?: (inspection: InspectionType) => void;
};

const statusBadgeClassName: Record<InspectionStatus, string> = {
	[InspectionStatus.SCHEDULED]:
		'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800',
	[InspectionStatus.IN_PROGRESS]:
		'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800',
	[InspectionStatus.COMPLETED]:
		'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800',
};

export function InspectionCard({ inspection, onRestore }: InpsectionCardProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const deletedView = !!onRestore;
	const onCardClick = () => {
		const path = `/assets/${inspection.assetId}/inspections/${inspection.id}`;
		navigate(deletedView ? deletedPreviewHref(path, 'inspection') : path);
	};
	const status = inspection.status || InspectionStatus.SCHEDULED;
	const damagesCount = inspection.damagesCount ?? 0;
	const filesCount = inspection.filesCount ?? 0;
	const damagesLabel = `${damagesCount} ${t('common.entities.damage', { count: damagesCount })}`;
	const filesLabel = `${filesCount} ${t('common.entities.file', { count: filesCount })}`;
	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: need
		// biome-ignore lint/a11y/useKeyWithClickEvents: need
		<div
			className="rounded-lg border text-card-foreground bg-card group hover:border-primary/50 cursor-pointer transition-colors shadow-sm"
			onClick={onCardClick}
		>
			<div className="p-4 flex justify-between items-center gap-4">
				<div className="min-w-0">
					<h4 className="font-medium group-hover:text-blue-700 flex items-center gap-2">
						<InspectionIcon
							className="size-4 shrink-0 text-muted-foreground"
							aria-label={t('common.entities.inspection', { count: 1 })}
						/>
						{inspection.title}
						{deletedView && <DeletedBadge />}
						{deletedView && <DeletedOnIcon deletedAt={inspection.deletedAt} deletedBy={inspection.deletedBy} />}
					</h4>
					<div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
						<div className="flex items-center gap-1">
							<Calendar className="h-3 w-3" />
							<span>{formatDateOnly(inspection.startDate || '')}</span>
						</div>
						<span className="">•</span>
						<span>{inspection.type}</span>
						{/* The counts are of live rows, always 0 for a deleted inspection; the restore dialog has the real ones */}
						{!deletedView && (
							<>
								<span className="">•</span>
								<span className="flex items-center gap-1" title={damagesLabel}>
									<DamageIcon className="h-3 w-3" aria-hidden="true" />
									<span aria-hidden="true">{damagesCount}</span>
									<span className="sr-only">{damagesLabel}</span>
								</span>
								<span
									className={cn('flex items-center gap-1', { 'text-primary font-medium': filesCount > 0 })}
									title={filesLabel}
									data-has-files={filesCount > 0}
								>
									<Paperclip className="h-3 w-3" aria-hidden="true" />
									<span aria-hidden="true">{filesCount}</span>
									<span className="sr-only">{filesLabel}</span>
								</span>
							</>
						)}
					</div>
				</div>
				<div className="flex items-center gap-4 shrink-0">
					<Badge className={cn('border px-3.5 py-0.5', statusBadgeClassName[status])}>
						{getInspectionStatusLabel(status)}
					</Badge>
					{deletedView ? (
						<Button
							variant="outline"
							size="sm"
							onClick={(event) => {
								// The card opens the inspection; this only restores
								event.stopPropagation();
								onRestore(inspection);
							}}
						>
							<RotateCcw />
							{t('common.actions.restore')}
						</Button>
					) : (
						<ChevronRight className="size-4" />
					)}
				</div>
			</div>
		</div>
	);
}
